import React from "react";
import {
  Typography,
  Button,
  CircularProgress,
  Divider,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import API from "../API";

const FIELD_LABELS = [
  ["applicationId", "Basvuru ID"],
  ["firstName", "Ad"],
  ["lastName", "Soyad"],
  ["email", "Email"],
  ["phone", "Telefon"],
  ["city", "Sehir"],
  ["position", "Pozisyon"],
  ["homeOffice", "Home Office"],
  ["workType", "Calisma Tipi"],
  ["startDate", "Baslama Tarihi"],
  ["status", "Durum"],
  ["note", "Not"],
];

const POSITION_LABELS = {
  "dijital-satis": "Dijital Satis",
};

const HOME_OFFICE_LABELS = {
  evet: "Evet",
  hayir: "Hayir",
};

const WORK_TYPE_LABELS = {
  herikisi: "Hibrit",
};

const STATUS_LABELS = {
  received: "Alindi",
  under_review: "Incelemede",
  interview: "Mulakat",
  offer: "Teklif",
  hired: "Ise Alindi",
  rejected: "Reddedildi",
  withdrawn: "Geri Cekildi",
  on_hold: "Beklemede",
};

const STATUS_OPTIONS = [
  "received",
  "under_review",
  "interview",
  "offer",
  "hired",
  "rejected",
  "withdrawn",
  "on_hold",
];

const STATUS_STYLES = {
  received: {
    backgroundColor: "#EFF6FF",
    color: "#1D4ED8",
    borderColor: "#BFDBFE",
  },
  under_review: {
    backgroundColor: "#FFFBEB",
    color: "#B45309",
    borderColor: "#FDE68A",
  },
  interview: {
    backgroundColor: "#EEF2FF",
    color: "#4338CA",
    borderColor: "#C7D2FE",
  },
  offer: {
    backgroundColor: "#ECFDF5",
    color: "#047857",
    borderColor: "#A7F3D0",
  },
  hired: {
    backgroundColor: "#DCFCE7",
    color: "#166534",
    borderColor: "#86EFAC",
  },
  rejected: {
    backgroundColor: "#FEF2F2",
    color: "#B91C1C",
    borderColor: "#FECACA",
  },
  withdrawn: {
    backgroundColor: "#F8FAFC",
    color: "#475569",
    borderColor: "#CBD5E1",
  },
  on_hold: {
    backgroundColor: "#FFF7ED",
    color: "#C2410C",
    borderColor: "#FED7AA",
  },
};

export default class CareerApplicationScreen extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      downloadingCv: false,
      statusDialogOpen: false,
      savingStatus: false,
      statusForm: {
        status: "received",
        comment: "",
      },
      application: undefined,
    };
  }

  componentDidMount() {
    const id = this.props.match.params.id;

    API.fetch("careers/application", {
      applicationId: id,
      applicationID: id,
    }).then((response) => {
      if (response?.error || response?.success === false) {
        this.props.history.goBack();
        return;
      }

      const application = response?.application;

      if (!application) {
        this.props.history.goBack();
        return;
      }

      this.setState({ application, loading: false });
    });
  }

  async downloadCv() {
    const { application } = this.state;

    if (!application?.cvUrl || this.state.downloadingCv) return;

    this.setState({ downloadingCv: true });

    try {
      const response = await fetch(application.cvUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = application.cv?.originalName || "cv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      window.open(application.cvUrl, "_blank", "noopener,noreferrer");
    }

    this.setState({ downloadingCv: false });
  }

  openStatusDialog() {
    this.setState({
      statusDialogOpen: true,
      statusForm: {
        status: this.state.application?.status || "received",
        comment: "",
      },
    });
  }

  closeStatusDialog() {
    if (this.state.savingStatus) return;

    this.setState({
      statusDialogOpen: false,
      statusForm: {
        status: this.state.application?.status || "received",
        comment: "",
      },
    });
  }

  saveStatus() {
    const { application, statusForm } = this.state;

    if (!application?._id || !statusForm.status || this.state.savingStatus) return;

    this.setState({ savingStatus: true });

    API.fetch("careers/applicationupdate", {
      _id: application._id,
      status: statusForm.status,
      comment: statusForm.comment,
    }).then((response) => {
      if (response?.error) {
        global.showPopup("Hata", response.message || "Durum guncellenemedi.");
        this.setState({ savingStatus: false });
        return;
      }

      this.setState({
        savingStatus: false,
        statusDialogOpen: false,
        application: {
          ...application,
          status: statusForm.status,
          lastComment: statusForm.comment,
          history: [
            ...(application.history || []),
            {
              status: statusForm.status,
              comment: statusForm.comment,
              previousStatus: application.status || null,
              changedAt: Date.now(),
              workerId: API.user?._id,
              workerName: API.user?.name || "",
              source: "panel",
            },
          ],
        },
        statusForm: {
          status: statusForm.status,
          comment: "",
        },
      });
    });
  }

  render() {
    if (this.state.loading) {
      return (
        <div
          style={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </div>
      );
    }

    const { application } = this.state;
    const fullName =
      [application.firstName, application.lastName].filter(Boolean).join(" ") ||
      "-";
    const currentStatus = application.status || "received";
    const statusStyle = getStatusStyle(currentStatus);
    const timelineItems = buildCareerTimeline(application);
    const summaryItems = [
      { label: "Pozisyon", value: formatFieldValue("position", application.position) },
      { label: "Sehir", value: formatPrimitive(application.city) },
      { label: "Calisma Tipi", value: formatFieldValue("workType", application.workType) },
      { label: "Baslama", value: formatPrimitive(application.startDate) },
    ];

    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ maxWidth: 960, width: "100%" }}>
          <Badge style={{ marginTop: 24, padding: 0, overflow: "hidden" }}>
            <div
              style={{
                padding: 24,
                borderBottom: "1px solid #E2E8F0",
                background:
                  "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 45%, #EEF2FF 100%)",
              }}
            >
              <Typography variant="h6">Aday Gecmisi</Typography>
              <Typography
                variant="body2"
                style={{ color: "#64748B", marginTop: 6 }}
              >
                Basvurunun surecte gectigi adimlar ve panel notlari.
              </Typography>
            </div>
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                padding: 24,
              }}
            >
              {timelineItems.map((item, index) => (
                <div
                  key={item.key}
                  style={{
                    minWidth: 220,
                    display: "flex",
                    alignItems: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          backgroundColor: item.style.color,
                          border: `4px solid ${item.style.backgroundColor}`,
                          boxSizing: "border-box",
                          flexShrink: 0,
                        }}
                      />
                      <div
                        style={{
                          height: 2,
                          flex: 1,
                          marginLeft: 10,
                          marginRight: 10,
                          backgroundColor:
                            index === timelineItems.length - 1
                              ? "transparent"
                              : "#CBD5E1",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        marginRight: 18,
                        border: `1px solid ${item.style.borderColor}`,
                        backgroundColor: item.style.backgroundColor,
                        borderRadius: 16,
                        padding: 16,
                        minHeight: 132,
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          color: item.style.color,
                          fontWeight: 700,
                          letterSpacing: 0.4,
                          textTransform: "uppercase",
                        }}
                      >
                        {item.badge}
                      </Typography>
                      <Typography
                        variant="body1"
                        style={{ fontWeight: 700, marginTop: 8, color: "#0F172A" }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ color: "#475569", marginTop: 8 }}
                      >
                        {item.dateLabel}
                      </Typography>
                      {item.workerName ? (
                        <Typography
                          variant="body2"
                          style={{ color: "#64748B", marginTop: 4 }}
                        >
                          {item.workerName}
                        </Typography>
                      ) : null}
                      {item.comment ? (
                        <Typography
                          variant="body2"
                          style={{
                            color: "#334155",
                            marginTop: 10,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {item.comment}
                        </Typography>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Badge>

          <Badge
            style={{
              marginTop: 24,
              background:
                "linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 45%, #EEF4FF 100%)",
              border: "1px solid #E2E8F0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <Typography variant="h4">{fullName}</Typography>
                  <StatusBadge status={currentStatus}>
                    {getStatusLabel(currentStatus)}
                  </StatusBadge>
                </div>
                <Typography
                  variant="body2"
                  style={{ color: "#6B7280", marginTop: 8, marginBottom: 16 }}
                >
                  {application.email || "-"}
                </Typography>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 12,
                  }}
                >
                  {summaryItems.map((item) => (
                    <div
                      key={item.label}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.8)",
                        border: "1px solid #E2E8F0",
                        borderRadius: 12,
                        padding: 14,
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{ color: "#64748B", marginBottom: 6 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {item.value}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                <Button variant="outlined" onClick={() => this.openStatusDialog()}>
                  Durumu Guncelle
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  disabled={!application.cvUrl}
                  onClick={() =>
                    window.open(application.cvUrl, "_blank", "noopener,noreferrer")
                  }
                >
                  CV Goruntule
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  disabled={!application.cvUrl || this.state.downloadingCv}
                  onClick={() => this.downloadCv()}
                >
                  CV Indir
                </Button>
              </div>
            </div>
            <div
              style={{
                marginTop: 18,
                padding: 16,
                borderRadius: 12,
                border: `1px solid ${statusStyle.borderColor}`,
                backgroundColor: statusStyle.backgroundColor,
              }}
            >
              <Typography
                variant="body2"
                style={{ color: "#64748B", marginBottom: 6 }}
              >
                Guncel Aday Durumu
              </Typography>
              <Typography
                variant="body1"
                style={{ color: statusStyle.color, fontWeight: 700 }}
              >
                {getStatusLabel(currentStatus)}
              </Typography>
              {getLatestComment(application) ? (
                <Typography
                  variant="body2"
                  style={{ marginTop: 8, color: "#334155", whiteSpace: "pre-wrap" }}
                >
                  {getLatestComment(application)}
                </Typography>
              ) : null}
            </div>
          </Badge>

          <Badge style={{ marginTop: 24, padding: 0 }}>
            <div style={{ padding: 24 }}>
              <Typography variant="h6">Aday Bilgileri</Typography>
            </div>
            <div style={{ padding: "0 24px 24px" }}>
              {FIELD_LABELS.map(([key, label], index) => (
                <React.Fragment key={key}>
                  {index > 0 ? <Divider /> : null}
                  <DetailRow label={label} value={formatFieldValue(key, application[key])} />
                </React.Fragment>
              ))}
            </div>
          </Badge>

          {getLatestComment(application) ? (
            <Badge style={{ marginTop: 24, padding: 0 }}>
              <div style={{ padding: 24 }}>
                <Typography variant="h6">Son Durum Yorumu</Typography>
              </div>
              <div style={{ padding: "0 24px 24px" }}>
                <Typography
                  variant="body1"
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {getLatestComment(application)}
                </Typography>
              </div>
            </Badge>
          ) : null}

          <Badge style={{ marginTop: 24, padding: 0 }}>
            <div style={{ padding: 24 }}>
              <Typography variant="h6">CV Dosyasi</Typography>
            </div>
            <div style={{ padding: "0 24px 24px" }}>
              <DetailRow
                label="Dosya Adi"
                value={application.cv?.originalName || "-"}
              />
              <Divider />
              <DetailRow label="Bucket" value={application.cv?.bucket || "-"} />
              <Divider />
              <DetailRow label="Dosya Yolu" value={application.cv?.path || "-"} />
            </div>
          </Badge>

          <Badge style={{ marginTop: 24, padding: 0 }}>
            <div style={{ padding: 24 }}>
              <Typography variant="h6">Egitim Bilgileri</Typography>
            </div>
            <div style={{ padding: "0 24px 24px" }}>
              {renderArraySection(application.educations, "Egitim kaydi bulunamadi.")}
            </div>
          </Badge>

          <Badge style={{ marginTop: 24, padding: 0 }}>
            <div style={{ padding: 24 }}>
              <Typography variant="h6">Deneyim Bilgileri</Typography>
            </div>
            <div style={{ padding: "0 24px 24px" }}>
              {renderArraySection(application.experiences, "Deneyim kaydi bulunamadi.")}
            </div>
          </Badge>

          <Dialog
            open={this.state.statusDialogOpen}
            onClose={() => this.closeStatusDialog()}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Aday durumunu guncelle</DialogTitle>
            <DialogContent>
              <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
                <TextField
                  select
                  label="Durum"
                  value={this.state.statusForm.status}
                  onChange={(e) =>
                    this.setState({
                      statusForm: {
                        ...this.state.statusForm,
                        status: e.target.value,
                      },
                    })
                  }
                >
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {getStatusLabel(status)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Yorum"
                  multiline
                  minRows={4}
                  value={this.state.statusForm.comment}
                  onChange={(e) =>
                    this.setState({
                      statusForm: {
                        ...this.state.statusForm,
                        comment: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.closeStatusDialog()} disabled={this.state.savingStatus}>
                Iptal
              </Button>
              <Button
                variant="contained"
                onClick={() => this.saveStatus()}
                disabled={this.state.savingStatus}
              >
                Kaydet
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    );
  }
}

function renderArraySection(items, emptyText) {
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <Typography variant="body2" style={{ color: "#6B7280" }}>
        {emptyText}
      </Typography>
    );
  }

  return items.map((item, index) => (
    <div
      key={index}
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: 16,
        marginTop: index === 0 ? 0 : 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <DescriptionIcon sx={{ color: "#6B7280" }} />
        <Typography variant="subtitle1">{getArrayItemTitle(item, index)}</Typography>
      </div>
      {Object.entries(item || {}).map(([key, value]) => (
        <DetailRow key={key} label={formatKey(key)} value={formatPrimitive(value)} compact />
      ))}
    </div>
  ));
}

function getArrayItemTitle(item, index) {
  return (
    item?.school ||
    item?.company ||
    item?.institution ||
    item?.title ||
    item?.position ||
    "Kayit " + (index + 1)
  );
}

function getStatusLabel(status) {
  if (!status) return "-";
  return STATUS_LABELS[status] || status;
}

function formatFieldValue(key, value) {
  if (key === "position") return POSITION_LABELS[value] || value || "-";
  if (key === "homeOffice") return HOME_OFFICE_LABELS[value] || value || "-";
  if (key === "workType") return WORK_TYPE_LABELS[value] || value || "-";
  if (key === "status") return getStatusLabel(value);
  return formatPrimitive(value);
}

function formatPrimitive(value) {
  if (value === undefined || value === null || value === "") return "-";
  if (Array.isArray(value)) return value.join(", ") || "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatKey(value) {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const DetailRow = ({ label, value, compact }) => {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        paddingTop: compact ? 8 : 16,
        paddingBottom: compact ? 8 : 16,
        alignItems: "flex-start",
      }}
    >
      <Typography
        variant="body2"
        style={{ width: compact ? 180 : 220, color: "#6B7280", flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </div>
  );
};

const StatusBadge = ({ status, children }) => {
  const style = getStatusStyle(status);

  return (
    <div
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${style.borderColor}`,
        backgroundColor: style.backgroundColor,
        color: style.color,
        fontWeight: 700,
        fontSize: 13,
        lineHeight: "18px",
      }}
    >
      {children}
    </div>
  );
};

const Badge = (props) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        boxShadow:
          "rgb(100 116 139 / 6%) 0px 1px 1px, rgb(100 116 139 / 10%) 0px 1px 2px",
        borderRadius: 8,
        padding: 24,
        color: "rgb(18, 24, 40)",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
};

function getStatusStyle(status) {
  return (
    STATUS_STYLES[status] || {
      backgroundColor: "#F8FAFC",
      color: "#334155",
      borderColor: "#CBD5E1",
    }
  );
}

function getLatestComment(application) {
  return application?.lastComment || application?.lastStatusComment || "";
}

function buildCareerTimeline(application) {
  const items = [
    {
      key: `created-${application?._id || application?.applicationId || "career"}`,
      badge: "Basvuru",
      title: "Basvuru alindi",
      comment: "",
      workerName: "",
      dateLabel: formatDateTime(application?.createdAt),
      style: getStatusStyle("received"),
    },
  ];

  (application?.history || []).forEach((entry, index) => {
    const status = entry?.status || "received";

    items.push({
      key: `${status}-${entry?.changedAt || index}`,
      badge: "Durum",
      title: getStatusLabel(status),
      comment: entry?.comment || "",
      workerName: entry?.workerName || "",
      dateLabel: formatDateTime(entry?.changedAt),
      style: getStatusStyle(status),
    });
  });

  return items;
}

function formatDateTime(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("tr-TR");
}
